<template>
  <div class="register-page">
    <!-- ========== LEFT PANEL: Branding ========== -->
    <div class="register-brand-panel">
      <!-- Background decorations -->
      <div class="brand-bg-shapes">
        <div class="brand-shape shape-1"></div>
        <div class="brand-shape shape-2"></div>
        <div class="brand-shape shape-3"></div>
      </div>

      <div class="brand-content">
        <!-- Logo -->
        <div class="brand-logo">
          <img src="@/assets/logo.png" alt="Logo" class="brand-logo-img" />
        </div>

        <!-- App name -->
        <p class="brand-app-name">{{ appTitle }}</p>

        <!-- Brand text -->
        <h1 class="brand-title">Empowering Abilities,<br />Transforming Lives.</h1>
      </div>
    </div>

    <!-- ========== RIGHT PANEL: Form ========== -->
    <div class="register-form-panel">
      <div class="form-wrapper">
        <!-- Mobile-only logo -->
        <div class="mobile-logo">
          <img src="@/assets/logo.png" alt="Logo" class="mobile-logo-img" />
          <h2 class="mobile-logo-title">{{ appTitle }}</h2>
        </div>

        <!-- Header -->
        <div class="form-header">
          <h2 class="form-title">Create your account</h2>
          <p class="form-description">Fill in your details to get started</p>
        </div>

        <!-- Form -->
        <form class="form-body" @submit.prevent="handleRegisterSubmit">
          <!-- Name Fields -->
          <div class="form-row-3">
            <BaseInput
              v-model="firstName"
              label="First name"
              type="text"
              placeholder="John"
              autocomplete="given-name"
              required
            />

            <BaseInput
              v-model="middleName"
              label="Middle name"
              type="text"
              placeholder="M. (optional)"
              autocomplete="additional-name"
            />

            <BaseInput
              v-model="lastName"
              label="Last name"
              type="text"
              placeholder="Doe"
              autocomplete="family-name"
              required
            />
          </div>

          <!-- Email -->
          <BaseInput
            v-model="email"
            label="Email address"
            type="email"
            placeholder="you@example.com"
            autocomplete="email"
            required
          />

          <!-- Phone -->
          <BaseInput
            v-model="phoneNumber"
            label="Phone number"
            type="tel"
            placeholder="+1 (555) 000-0000 (optional)"
            autocomplete="tel"
          />

          <!-- Address -->
          <BaseInput
            v-model="address"
            label="Address"
            type="text"
            placeholder="123 Main St, City, State (optional)"
            autocomplete="street-address"
          />

          <!-- Password Fields -->
          <div class="form-row-2">
            <BaseInput
              v-model="password"
              label="Password"
              type="password"
              placeholder="••••••••"
              autocomplete="new-password"
              required
            />

            <BaseInput
              v-model="passwordConfirmation"
              label="Confirm password"
              type="password"
              placeholder="••••••••"
              autocomplete="new-password"
              required
            />
          </div>

          <!-- Submit -->
          <BaseButton
            type="submit"
            variant="primary"
            size="lg"
            :loading="loading"
            fullWidth
          >
            {{ loading ? 'Creating account...' : 'Create account' }}
          </BaseButton>
        </form>

        <!-- Sign in link -->
        <p class="form-footer mt-8">
          Already have an account?
          <router-link to="/login" class="form-footer-link">
            Sign in
          </router-link>
        </p>
      </div>
    </div>

    <!-- Success Modal -->
    <BaseModal
      v-model="showSuccessModal"
      size="md"
      :show-close="false"
    >
      <div class="success-modal-content">
        <!-- Success Icon -->
        <div class="success-icon-wrapper">
          <div class="success-icon">
            <CheckCircle class="h-12 w-12" />
          </div>
        </div>

        <!-- Title -->
        <h2 class="success-title">Registration Successful!</h2>

        <!-- Messages -->
        <div class="success-messages">
          <p class="success-message-primary">
            Your account has been created successfully!
          </p>
          <div class="pending-badge">
            <Clock class="h-4 w-4" />
            <span>Pending Approval</span>
          </div>
          <p class="success-message-secondary">
            Your account is currently pending approval from an administrator. You will be notified once your account has been approved.
          </p>
        </div>
      </div>

      <template #footer>
        <div class="modal-footer-buttons">
          <button 
            type="button"
            class="modal-btn modal-btn-ghost"
            @click="goToLogin"
          >
            Cancel
          </button>
          <button 
            type="button"
            class="modal-btn modal-btn-primary"
            @click="goToLogin"
          >
            Go to Login Now
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { CheckCircle, Clock } from 'lucide-vue-next'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import { useRegister } from '@/composables/auth/useRegister'

const router = useRouter()
const appTitle = computed(() => process.env.VUE_APP_TITLE || 'SP Template')
const showSuccessModal = ref(false)

const {
  firstName,
  middleName,
  lastName,
  email,
  phoneNumber,
  address,
  password,
  passwordConfirmation,
  loading,
  error,
  success,
  handleRegister
} = useRegister()

const handleRegisterSubmit = async () => {
  const result = await handleRegister()
  if (result) {
    showSuccessModal.value = true
  }
}

const goToLogin = () => {
  showSuccessModal.value = false
  router.push('/login')
}
</script>

<style scoped>
/* ============================================
   PAGE LAYOUT
   ============================================ */

.register-page {
  @apply min-h-screen flex;
}

/* ============================================
   LEFT PANEL: BRANDING
   ============================================ */

.register-brand-panel {
  @apply relative hidden lg:flex lg:w-5/12 xl:w-5/12
         bg-gradient-to-br from-primary via-primary-hover to-primary-active
         dark:from-gray-900 dark:via-primary-dark-bg dark:to-gray-900
         overflow-hidden;
}

/* Background shapes */
.brand-bg-shapes {
  @apply absolute inset-0 overflow-hidden pointer-events-none;
}

.brand-shape {
  @apply absolute rounded-full opacity-10;
}

.shape-1 {
  @apply w-96 h-96 -top-20 -left-20
         bg-white dark:bg-primary-dark;
  animation: float 20s ease-in-out infinite;
}

.shape-2 {
  @apply w-80 h-80 bottom-20 right-10
         bg-white dark:bg-primary-dark;
  animation: float 25s ease-in-out infinite reverse;
}

.shape-3 {
  @apply w-64 h-64 top-1/2 left-1/3
         bg-white dark:bg-primary-dark/50;
  animation: float 30s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

/* Brand content */
.brand-content {
  @apply relative z-10 flex flex-col justify-center
         px-12 xl:px-16 text-white;
}

.brand-logo {
  @apply mb-8;
}

.brand-logo-img {
  @apply h-12 w-12 rounded-2xl object-contain;
}

.brand-app-name {
  @apply text-lg font-bold uppercase tracking-widest
         text-white/90 mb-4;
}

.brand-title {
  @apply text-3xl xl:text-4xl font-bold tracking-tight mb-4;
}

.brand-subtitle {
  @apply text-base xl:text-lg text-white/80 leading-relaxed max-w-md;
}

/* Features */
.brand-features {
  @apply mt-10 space-y-4;
}

.brand-feature {
  @apply flex items-center gap-3;
}

.feature-icon {
  @apply h-8 w-8 flex items-center justify-center
         rounded-lg bg-white/20 backdrop-blur-sm;
}

/* ============================================
   RIGHT PANEL: FORM
   ============================================ */

.register-form-panel {
  @apply flex-1 flex items-center justify-center
         bg-gray-50 dark:bg-gray-900
         px-4 py-8 sm:px-6 sm:py-8 lg:px-8
         overflow-y-auto;
}

.form-wrapper {
  @apply w-full max-w-md space-y-5;
}

.mobile-logo {
  @apply flex flex-col items-center gap-3 mb-6 lg:hidden
         rounded-2xl py-6
         bg-gradient-to-br from-primary/5 via-primary/10 to-primary-hover/5
         dark:from-primary-dark/10 dark:via-primary-dark/5 dark:to-primary-dark/10;
}

.mobile-logo-img {
  @apply h-14 w-14 rounded-2xl object-contain;
}

.mobile-logo-title {
  @apply text-base font-bold text-primary dark:text-primary-dark uppercase tracking-wider;
}

/* Form header */
.form-header {
  @apply text-center lg:text-left;
}

.form-title {
  @apply text-2xl font-bold
         text-gray-900 dark:text-white;
}

.form-description {
  @apply mt-2 text-sm
         text-gray-600 dark:text-gray-400;
}

/* Form body */
.form-body {
  @apply space-y-4;
}

/* 2-column row */
.form-row-2 {
  @apply grid grid-cols-1 sm:grid-cols-2 gap-4;
}

/* 3-column row */
.form-row-3 {
  @apply grid grid-cols-1 sm:grid-cols-3 gap-4;
}

/* Form footer */
.form-footer {
  @apply text-center text-sm
         text-gray-600 dark:text-gray-400;
}

.form-footer-link {
  @apply font-semibold text-primary dark:text-primary-dark
         hover:text-primary-hover dark:hover:text-primary-dark
         transition-colors duration-200
         cursor-pointer relative z-10;
}

/* ============================================
   SUCCESS MODAL
   ============================================ */

.success-modal-content {
  @apply text-center px-2 py-4;
}

.success-icon-wrapper {
  @apply flex justify-center mb-6;
}

.success-icon {
  @apply h-20 w-20 rounded-full
         bg-gradient-to-br from-success/20 to-success/10
         dark:from-success-dark/20 dark:to-success-dark/10
         flex items-center justify-center
         text-success dark:text-success-dark
         animate-bounce;
}

.success-title {
  @apply text-2xl font-bold
         text-gray-900 dark:text-white
         mb-4;
}

.success-messages {
  @apply space-y-3 mb-6;
}

.success-message-primary {
  @apply text-base text-gray-700 dark:text-gray-300;
}

.pending-badge {
  @apply inline-flex items-center gap-2
         px-4 py-2 rounded-full
         bg-warning/10 dark:bg-warning-dark/10
         text-warning dark:text-warning-dark
         text-sm font-semibold;
}

.success-message-secondary {
  @apply text-sm text-gray-600 dark:text-gray-400
         leading-relaxed;
}

.modal-footer-buttons {
  @apply flex gap-3 justify-end;
}

.modal-btn {
  @apply px-6 py-2.5 rounded-xl font-semibold text-sm
         transition-all duration-200
         cursor-pointer
         focus:outline-none focus:ring-2 focus:ring-offset-2;
}

.modal-btn-ghost {
  @apply bg-transparent
         text-gray-700 dark:text-gray-300
         hover:bg-gray-100 dark:hover:bg-gray-700
         focus:ring-gray-500;
}

.modal-btn-primary {
  @apply bg-primary
         text-white
         hover:bg-primary-hover
         focus:ring-primary
         shadow-md;
}

/* ============================================
   RESPONSIVE
   ============================================ */

@media (max-width: 1024px) {
  .register-brand-panel {
    @apply hidden;
  }
}

@media (min-width: 640px) {
  .form-wrapper {
    @apply max-w-lg;
  }
}
</style>
